import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import api, { uploadFile } from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Edit3, Trash2, Users, X, Upload, Globe, Instagram, Youtube, Facebook, Music2, Image, Search, Loader } from 'lucide-react';

export function ArtistModal({ artist, userId, onClose, onSaved }) {
  const [form, setForm] = useState(artist || { name: '', spotify_uri: '', apple_music_id: '', photo_path: '', biography: '', link_instagram: '', link_facebook: '', link_youtube: '', link_tiktok: '', link_website: '', link_twitter: '' });
  const [saving, setSaving] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(artist?.photo_path ? `/uploads/artwork/${userId}/${artist.photo_path}` : null);
  const [uploading, setUploading] = useState(false);
  const [extResults, setExtResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showExt, setShowExt] = useState(false);
  const searchTimer = useRef(null);

  // Search external when name changes (debounced)
  const onNameChange = (val) => {
    setForm(p => ({ ...p, name: val }));
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (val.length < 2) { setExtResults([]); setShowExt(false); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/artists/autocomplete?q=${encodeURIComponent(val)}`);
        setExtResults(data.external || []);
        setShowExt((data.external || []).length > 0);
      } catch (e) {}
      finally { setSearching(false); }
    }, 400);
  };

  const pickExternal = (ext) => {
    setForm(p => ({
      ...p,
      name: ext.name,
      spotify_uri: ext.spotify_uri || p.spotify_uri || '',
      apple_music_id: ext.apple_music_id || p.apple_music_id || ''
    }));
    setShowExt(false);
    setExtResults([]);
    toast.success(`${ext.name} — dati piattaforme compilati`);
  };

  const handlePhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const { data } = await uploadFile('/uploads/artwork', f, 'artwork');
      setForm(p => ({ ...p, photo_path: data.file.filename }));
      setPhotoPreview(URL.createObjectURL(f));
      toast.success('Foto caricata');
    } catch (err) { toast.error('Errore upload'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error('Nome artista obbligatorio');
    setSaving(true);
    try {
      if (artist?.id) {
        await api.put(`/artists/${artist.id}`, form);
        toast.success('Artista aggiornato!');
        onSaved(null);
      } else {
        const { data } = await api.post('/artists', form);
        toast.success('Artista creato!');
        onSaved(data.artist);
      }
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(`Artista simile esistente. Creare comunque "${form.name}"?`)) {
          try { const { data } = await api.post('/artists/force', form); onSaved(data.artist); onClose(); }
          catch (e2) { toast.error('Errore'); }
        }
      } else { toast.error(err.response?.data?.error || 'Errore'); }
    }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-lg">{artist?.id ? 'Modifica Artista' : 'Nuovo Artista'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          {/* Photo - with dimension note */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed overflow-hidden flex-shrink-0 flex items-center justify-center">
              {uploading ? <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-pn-red" /> : photoPreview ? <img src={photoPreview} alt="" className="w-full h-full object-cover" /> : <Image size={24} className="text-gray-300" />}
            </div>
            <div>
              <input type="file" accept="image/*" className="hidden" id="artist-photo-modal" onChange={handlePhoto} />
              <label htmlFor="artist-photo-modal" className="btn-secondary text-xs cursor-pointer"><Upload size={14} className="inline mr-1" />Foto Artista</label>
              {photoPreview && <button onClick={() => { setForm(p => ({ ...p, photo_path: '' })); setPhotoPreview(null); }} className="text-xs text-red-400 hover:underline ml-2">Rimuovi</button>}
              <p className="text-[10px] text-gray-400 mt-1">JPG/PNG, consigliato 500×500px, quadrata</p>
            </div>
          </div>

          {/* Name + external search */}
          <div className="relative">
            <label className="block text-sm font-medium mb-1">Nome Artista *</label>
            <div className="relative">
              <input className="input-field pr-8" value={form.name} onChange={e => onNameChange(e.target.value)} autoFocus placeholder="Scrivi il nome dell'artista..." />
              {searching && <Loader size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />}
            </div>
            {/* External results dropdown */}
            {showExt && extResults.length > 0 && (
              <div className="absolute z-30 top-full left-0 right-0 bg-white border rounded-xl shadow-xl mt-1 max-h-48 overflow-y-auto">
                <div className="px-3 py-1.5 bg-green-50 text-[10px] text-green-600 font-bold uppercase sticky top-0 border-b">Trovati sulle piattaforme</div>
                {extResults.map((ex, i) => (
                  <button key={i} onClick={() => pickExternal(ex)} className="w-full text-left px-3 py-2.5 hover:bg-green-50 text-sm border-b last:border-0 flex items-center gap-3">
                    {ex.photo_url?<img src={ex.photo_url} className="w-8 h-8 rounded-full object-cover flex-shrink-0" alt=""/>:<div className="w-8 h-8 rounded-full bg-green-100 text-green-600 text-xs flex items-center justify-center flex-shrink-0 font-bold">{ex.spotify_uri?'S':ex.apple_music_id?'A':'♪'}</div>}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{ex.name}</p>
                      <p className="text-[10px] text-gray-400">
                        {ex.apple_music_id ? 'Apple Music' : ''}{ex.spotify_uri && ex.apple_music_id ? ' · ' : ''}{ex.spotify_uri ? 'Spotify' : ''}
                        {ex.followers ? ` · ${ex.followers.toLocaleString()} followers` : ''}
                        {ex.genres?.length ? ` · ${ex.genres[0]}` : ''}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div><label className="block text-sm font-medium mb-1">Biografia</label><textarea className="input-field h-20 resize-y" value={form.biography || ''} onChange={e => setForm(p => ({ ...p, biography: e.target.value }))} placeholder="Breve biografia..." /></div>

          <div className="border-t pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Piattaforme Musicali</p>
            <div className="space-y-2">
              <div><label className="block text-xs text-gray-500 mb-1">Spotify URI</label><input className="input-field" value={form.spotify_uri || ''} onChange={e => setForm(p => ({ ...p, spotify_uri: e.target.value }))} placeholder="spotify:artist:4Z8W4fKeB5YxbusRsdQVPb" /><p className="text-[10px] text-gray-400">Spotify → Profilo artista → Condividi → Copia URI</p></div>
              <div><label className="block text-xs text-gray-500 mb-1">Apple Music ID</label><input className="input-field" value={form.apple_music_id || ''} onChange={e => setForm(p => ({ ...p, apple_music_id: e.target.value }))} placeholder="https://music.apple.com/artist/nome/123456" /><p className="text-[10px] text-gray-400">Apple Music → Profilo artista → Copia URL</p></div>
            </div>
          </div>
          <div className="border-t pt-3">
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Link & Social</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2"><Globe size={14} className="text-gray-400" /><input className="input-field" value={form.link_website || ''} onChange={e => setForm(p => ({ ...p, link_website: e.target.value }))} placeholder="https://..." /></div>
              <div className="flex items-center gap-2"><Instagram size={14} className="text-gray-400" /><input className="input-field" value={form.link_instagram || ''} onChange={e => setForm(p => ({ ...p, link_instagram: e.target.value }))} placeholder="https://instagram.com/..." /></div>
              <div className="flex items-center gap-2"><Facebook size={14} className="text-gray-400" /><input className="input-field" value={form.link_facebook || ''} onChange={e => setForm(p => ({ ...p, link_facebook: e.target.value }))} placeholder="https://facebook.com/..." /></div>
              <div className="flex items-center gap-2"><Youtube size={14} className="text-gray-400" /><input className="input-field" value={form.link_youtube || ''} onChange={e => setForm(p => ({ ...p, link_youtube: e.target.value }))} placeholder="https://youtube.com/@..." /></div>
              <div className="flex items-center gap-2"><Music2 size={14} className="text-gray-400" /><input className="input-field" value={form.link_tiktok || ''} onChange={e => setForm(p => ({ ...p, link_tiktok: e.target.value }))} placeholder="https://tiktok.com/@..." /></div>
            </div>
          </div>
        </div>
        <div className="flex gap-3 mt-5"><button onClick={onClose} className="btn-secondary flex-1">Annulla</button><button onClick={save} disabled={saving} className="btn-primary flex-1">{saving ? '...' : artist?.id ? 'Salva Modifiche' : 'Crea Artista'}</button></div>
      </div>
    </div>
  );
}

export default function ArtistsPage() {
  const { user } = useAuth();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, artist: null });

  const load = async () => { try { const { data } = await api.get('/artists'); setArtists(data.artists); } catch (e) {} finally { setLoading(false); } };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm('Eliminare questo artista?')) return;
    try { await api.delete(`/artists/${id}`); toast.success('Eliminato'); load(); } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
  };

  return (
    <div>
      {modal.open && <ArtistModal artist={modal.artist} userId={user?.id} onClose={() => setModal({ open: false, artist: null })} onSaved={() => { setModal({ open: false, artist: null }); load(); }} />}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-pn-dark">I Miei Artisti</h1>
        <button onClick={() => setModal({ open: true, artist: null })} className="btn-primary inline-flex items-center gap-2"><Plus size={18} />Nuovo Artista</button>
      </div>
      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pn-red" /></div> : artists.length === 0 ? (
        <div className="text-center py-16"><Users size={48} className="mx-auto text-gray-300 mb-4" /><p className="text-gray-400">Nessun artista. Creane uno!</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {artists.map(a => (
            <div key={a.id} className="card p-5 hover:shadow-md transition">
              <div className="flex items-start gap-3">
                {a.photo_path ? <img src={`/uploads/artwork/${user?.id}/${a.photo_path}`} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-pn-red flex items-center justify-center text-white font-bold">{a.name?.[0]}</div>}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-pn-dark">{a.name}</h3>
                  {a.biography && <p className="text-xs text-gray-400 mt-1 line-clamp-2">{a.biography}</p>}
                  <div className="flex gap-1 mt-1">
                    {a.spotify_uri && <span className="text-[9px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded">Spotify ✓</span>}
                    {a.apple_music_id && <span className="text-[9px] bg-pink-50 text-pink-600 px-1.5 py-0.5 rounded">Apple ✓</span>}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                <button onClick={() => setModal({ open: true, artist: a })} className="text-xs text-blue-500 hover:underline flex items-center gap-1"><Edit3 size={12} />Modifica</button>
                <button onClick={() => del(a.id)} className="text-xs text-red-400 hover:underline flex items-center gap-1"><Trash2 size={12} />Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
